import { type DOMNode, type Element } from 'html-react-parser';

import { type InlineType } from './types';

export function isElement(node: DOMNode): node is Element {
  return node.type === ('tag' as Element['type']);
}

function hasDescendantTag(node: Element, tagName: string): boolean {
  for (const child of node.children as DOMNode[]) {
    if (!isElement(child)) continue;
    if (child.name === tagName) return true;
    if (hasDescendantTag(child, tagName)) return true;
  }
  return false;
}

function isYouTubeSrc(src: string): boolean {
  return src.includes('youtube.com') || src.includes('youtube-nocookie.com');
}

function hasYouTubeIframe(node: Element): boolean {
  for (const child of node.children as DOMNode[]) {
    if (!isElement(child)) continue;
    if (child.name === 'iframe' && isYouTubeSrc(child.attribs?.src ?? '')) return true;
    if (hasYouTubeIframe(child)) return true;
  }
  return false;
}

// Classifies a single direct child of an <inline> element.
// 'gallery' is never returned here — InlineContentRenderer promotes consecutive
// 'image' children to a gallery group based on count.
export function detectChildType(child: Element): Exclude<InlineType, 'gallery'> {
  if (child.name === 'a') {
    const isIframe = child.attribs?.['data-type'] === 'iframe';
    const href = child.attribs?.href ?? '';
    if (isIframe && (href.includes('google.com/maps') || href.includes('maps.google'))) return 'map';
    if ('data-fancybox' in (child.attribs ?? {})) return 'image';
    return 'unsupported';
  }
  if (child.name === 'figure') {
    if (hasDescendantTag(child, 'video')) return 'video';
    if (hasYouTubeIframe(child)) return 'youtube';
    if (hasDescendantTag(child, 'img')) return 'figure';
    return 'unsupported';
  }
  return 'unsupported';
}
